import { ng } from '@uirouter/angularjs/lib/angular';
import * as angular from 'angular';
import { ngStorage } from 'ngstorage';
import { ILanguageSettings } from '../../../app.admin/modules/model/ILanguageSettings';
import { HttpService } from '../../../app.common/modules/api/http.service';
import { RetailerService } from '../api/retailer.service';
import { ForgotPassword, ResetPassword, RetailerChangeEmail, RetailerChangePassword, RetailerProfile } from '../core';
import { ILogIn, IUser } from './model';
import { IResetResponse } from './model/reset-response.model';

export class AccountService {

    public static $inject = ['$rootScope', '$localStorage', 'http', 'retailerService', '$timeout', '$window', '$translate'];

    public userData: IUser = {
        id: null,
        isAuth: false,
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: {
            PhoneNumber: null,
            CountryPhoneCodeID: null
        },
        whatsAppNumber: {
            PhoneNumber: null,
            CountryPhoneCodeID: null
        },
        retailerStatus: null,
        showGetStarted: null,
        permission: null,
        viewedPrivacyNotification: null,
        currencyID: null,
        currencySign: null,
        countryID: null,
        countryName: null,
        languageID: null,
        retailerType: null,
        isEmpty(): boolean {
            return this.email === null || this.firstName === null || this.showGetStarted === undefined;
        },
        cleanUp() {
            this.isAuth = false;
            this.firstName = '';
            this.lastName = '';
            this.email = '';
            this.phone = '';
            this.retailerStatus = null;
            this.showGetStarted = null;
            this.permission = null;
            this.id = null;
            this.viewedPrivacyNotification = null;
            this.currencyID = null;
            this.currencySign = null;
            this.countryID = null;
            this.countryName = null;
            this.languageID = null;
        }
    };

    public homePage = '/business';
    public loginPageState = 'account.login';

    constructor(
        private $rootScope: ng.IRootScopeService & any, // TODO
        private $localStorage: ngStorage.StorageService,
        private http: HttpService,
        private retailerService: RetailerService,
        private $timeout: ng.ITimeoutService,
        private $window: ng.IWindowService,
        private $translate: angular.translate.ITranslateService
    ) {
    }

    public logOut(redirectToHomepage?: boolean): void {
        this.clearAuthData();
        this.$rootScope.$broadcast('LogOut');

        if (redirectToHomepage) {
            this.$timeout(() => {
                this.$window.location.href = this.homePage;
            }, 100);
        } else {
            this.$rootScope.$state.go(this.loginPageState);
        }
    }

    public clearAuthData(): void {
        delete this.$localStorage.retailerAuthorizationData;
        delete this.$localStorage.recentlyUsedClassificationCategories;
        this.userData.cleanUp();
    }

    public logIn(loginData: ILogIn) {
        let params: string = '';
        if (loginData.adminLogin) {
            params = `grant_type=admin_token&email=${encodeURIComponent(loginData.userName)}&token=${encodeURIComponent(this.$localStorage.adminAuthorizationData.token)}`;
        } else {
            params = `grant_type=password&username=${encodeURIComponent(loginData.userName)}&password=${encodeURIComponent(loginData.password)}`;
        }

        return this.http.post(this.retailerService.auth(), params, {headers: {'Content-Type': 'application/x-www-form-urlencoded'}})
            .then((response: any) => {// TODO
                if (response === 'cancel') {
                    return null;
                }
                this.$localStorage.retailerAuthorizationData = {token: response.access_token};
                this.$localStorage.recentlyUsedClassificationCategories = [];
                this.userData.isAuth = true;
                this.$rootScope.$broadcast('Login');
                return response;
            });
    }

    public isLoggedInAsAdmin() {
        return this.http.get(this.retailerService.loggedInAsAdmin());
    }

    public getProfile() {
        return this.http.get(this.retailerService.profile()).then((profile: RetailerProfile) => {// TODO
            this.$localStorage.retailerAuthorizationData = {
                token: this.$localStorage.retailerAuthorizationData.token,
                id: profile.ID,
                firstName: profile.FirstName,
                lastName: profile.LastName,
                permission: profile.Permission,
                countryID:  profile.CountryID
            };
            this.$localStorage.BusinessUnionType = profile.BusinessUnionType;
            this.userData.id = profile.ID;
            this.userData.firstName = profile.FirstName;
            this.userData.lastName = profile.LastName;
            this.userData.retailerStatus = profile.RetailerStatus;
            this.userData.email = profile.Email;
            this.userData.phoneNumber = profile.PhoneNumber;
            this.userData.whatsAppNumber = profile.WhatsAppNumber;
            this.userData.isAuth = true;
            this.userData.showGetStarted = profile.ShowGetStarted;
            this.userData.permission = profile.Permission;
            this.userData.viewedPrivacyNotification = profile.ViewedPrivacyNotification;
            this.userData.currencySign = profile.CurrencySign;
            this.userData.currencyID = profile.CurrencyID;
            this.userData.countryID = profile.CountryID;
            this.userData.countryName = profile.CountryName;
            this.userData.languageID = profile.LanguageID;
            this.userData.retailerType = profile.RetailerType;

            if (profile.LanguageID) {
                this.setLocalization(profile.LanguageID);
            }

            return profile;
        });
    }

    public setLocalization(languageID: number) {
        this.getLanguages()
            .then((languages) => {
                const userLanguage = languages.find((x) => x.ID === languageID);
                this.$translate.use(userLanguage.IsoCode)
                    .catch(() => console.log('invalid language code'));
            })
            .catch((e) => console.log(e));
    }

    public getLanguages() {
        return this.http.get<ILanguageSettings[]>(this.retailerService.languages());
    }

    public updateProfile(profile: RetailerProfile) {
        return this.http.put(this.retailerService.profile(), profile).then(() => {
            this.$localStorage.retailerAuthorizationData = {
                id: profile.ID,
                token: this.$localStorage.retailerAuthorizationData.token,
                firstName: profile.FirstName,
                lastName: profile.LastName,
                permission: profile.Permission,
                countryID: profile.CountryID
            };
            this.userData.firstName = profile.FirstName;
            this.userData.lastName = profile.LastName;
            this.userData.languageID = profile.LanguageID;
        });
    }

    public updateViewedPrivacyNotification() {
        return this.http.get(this.retailerService.viewedPrivacyNotification());
    }

    public getEmail() {
        return this.http.get(this.retailerService.profileEmail());
    }

    public updateEmail(model: RetailerChangeEmail) {
        return this.http.put(this.retailerService.profileEmail(), model);
    }

    public forgotPassword(model: ForgotPassword) {
        return this.http.post(this.retailerService.forgotPassword(), model);
    }

    public resetPassword(model: ResetPassword) {
        return this.http.post<IResetResponse>(this.retailerService.resetPassword(), model);
    }

    public updatePassword(model: RetailerChangePassword) {
        return this.http.put(this.retailerService.profilePassword(), model);
    }

    public updateRetailerToken(tokenData: string) {
        this.$localStorage.retailerAuthorizationData = { token: tokenData };
        this.$localStorage.recentlyUsedClassificationCategories = [];
        this.userData.isAuth = true;
    }

    public fillAuthData() {
        const authData = this.$localStorage.retailerAuthorizationData;
        if (angular.isDefined(authData)) {
            this.userData.isAuth = true;
        }
    }

    public isRetailerTokenPresents(): boolean {
        const authData = this.$localStorage.retailerAuthorizationData;
        return angular.isDefined(authData);
    }

    public getRetailerToken() {
        return this.$localStorage.retailerAuthorizationData.token;
    }

    public addProfileImage() {
        return this.retailerService.setProfileImage();
    }

    public getProfileImage(imageId: number, width?: number, height?: number) {
        return this.retailerService.getProfileImage(imageId, width, height);
    }

    public deleteProfileImage(imageId: string) {
        return this.http.delete(this.retailerService.deleteProfileImage(imageId));
    }
}

angular.module('naut').service('accountService', AccountService);



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/app.retailer/modules/account/account.service.ts